import { EventDetails } from '@/feature/event-details-sbk/types';
import { getTeamColors } from '@/feature/event-details-sbk/utils/get-team-colors';
import { TeamSide, getTeamDisplayName } from '@/utils/sport-utils';

import { GridPeriodScore, PeriodScore, ScoreboardEvent, StoreCacheSlice, TeamInfo } from '../types';

/**
 * Build the `TeamInfo` pair (team1 = top row, team2 = bottom row) plus the per-side data lookups
 * each sport's builder needs to map breakdown / score data to the team-order display.
 */
export const buildTeamInfo = (
    event: ScoreboardEvent,
    firstSide: TeamSide,
    secondSide: TeamSide
): {
    team1: TeamInfo;
    team2: TeamInfo;
} => {
    const colors = getTeamColors(event);
    const teamData = { away: event.away_team, home: event.home_team };

    const team1: TeamInfo = {
        icon: teamData[firstSide].logo_url ?? '',
        shortCode: getTeamDisplayName(teamData[firstSide], event.sport.name),
        colors: [colors[firstSide].primary, colors[firstSide].secondary],
        shouldShowFallbackLogo: colors[firstSide].shouldShowFallbackLogo,
    };
    const team2: TeamInfo = {
        icon: teamData[secondSide].logo_url ?? '',
        shortCode: getTeamDisplayName(teamData[secondSide], event.sport.name),
        colors: [colors[secondSide].primary, colors[secondSide].secondary],
        shouldShowFallbackLogo: colors[secondSide].shouldShowFallbackLogo,
    };

    return { team1, team2 };
};

/** Translate a position-agnostic `GridPeriodScore` into a `PeriodScore` with team1/team2 ordering. */
export const toPeriodScore = (g: GridPeriodScore, firstSide: TeamSide): PeriodScore => ({
    team1Score: firstSide === 'away' ? g.awayScore : g.homeScore,
    team2Score: firstSide === 'away' ? g.homeScore : g.awayScore,
    team1SubScore: firstSide === 'away' ? g.awaySubScore : g.homeSubScore,
    team2SubScore: firstSide === 'away' ? g.homeSubScore : g.awaySubScore,
    periodNumber: g.periodNumber,
    label: g.label ?? g.periodNumber,
});

/**
 * Re-shape the scoreboard `StoreCacheSlice` into an `EventDetails`-compatible object so we can
 * reuse the existing `getScoreboard` / `getFinalScoreColumns` utilities without forking their APIs.
 */
export const cacheToEventDetails = (event: ScoreboardEvent, cache: StoreCacheSlice): EventDetails => ({
    id: event.id,
    period_scores: cache.periodScores,
    period_name: cache.periodName,
    home_score: cache.homeScore,
    away_score: cache.awayScore,
    sport_specific: cache.sportSpecific,
});
